import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ComputerDesktopIcon, 
  CameraIcon,
  ChevronDownIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';

export const ProductsDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const dropdownRef = useRef(null);
  const timeoutRef = useRef(null);

  const products = [
    {
      id: 1,
      name: "DICOMFiles",
      description: "Automatización completa del flujo DICOM",
      shortDesc: "Reduce trabajo manual en 80-90%",
      icon: <ComputerDesktopIcon className="h-6 w-6" />,
      link: "/dicomfiles",
      gradient: "from-blue-500 to-blue-600",
      features: ["Procesamiento 24/7", "Integración PACS", "Automatización PDFs"]
    },
    {
      id: 2,
      name: "DICOMCapture",
      description: "Captura y procesamiento avanzado",
      shortDesc: "Captura desde equipos médicos",
      icon: <CameraIcon className="h-6 w-6" />,
      link: "/dicomcapture",
      gradient: "from-green-500 to-green-600",
      features: ["Captura automática", "Validación calidad", "Tiempo real"]
    }
  ];

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsHovered(true);
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 150);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setIsHovered(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <div 
      ref={dropdownRef}
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Trigger Button */}
      <button className="flex items-center text-gray-600 hover:text-blue-600 px-3 py-2 text-sm font-medium transition-all duration-200 border-b-2 border-transparent hover:border-blue-600">
        Productos
        <ChevronDownIcon className={`ml-1 h-4 w-4 transition-transform duration-200 ${
          isOpen ? 'rotate-180' : ''
        }`} />
      </button>

      {/* Dropdown Menu */}
      <div className={`absolute top-full left-1/2 transform -translate-x-1/2 w-[800px] max-w-[95vw] bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden transition-all duration-300 ease-out origin-top ${
        isOpen 
          ? 'opacity-100 translate-y-0 scale-y-100' 
          : 'opacity-0 -translate-y-2 scale-y-0 pointer-events-none'
      }`}>
        {/* Products Grid - Horizontal Layout */}
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {products.map((product, index) => (
              <Link
                key={product.id}
                to={product.link}
                className={`group block p-4 rounded-xl hover:bg-gray-50 transition-all duration-300 border border-transparent hover:border-gray-200 ${
                  isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
                }`}
                style={{
                  transitionDelay: isOpen ? `${100 + (index * 100)}ms` : '0ms'
                }}
              >
                {/* Icon/Image Container */}
                <div className="flex justify-center mb-4">
                  <div className={`p-4 rounded-xl bg-gradient-to-r ${product.gradient} text-white transition-transform duration-200 group-hover:scale-110 shadow-lg`}>
                    {product.icon}
                  </div>
                </div>

                {/* Content */}
                <div className="text-center">
                  <h4 className="text-lg font-bold text-gray-900 group-hover:text-blue-600 transition-colors duration-200 mb-2">
                    {product.name}
                  </h4>
                  
                  <p className="text-sm text-gray-600 mb-2 leading-relaxed">
                    {product.description}
                  </p>
                  
                  <p className="text-xs text-gray-500 mb-3">
                    {product.shortDesc}
                  </p>

                  {/* Features */}
                  <div className="space-y-1 mb-3">
                    {product.features.map((feature, featureIndex) => (
                      <div 
                        key={featureIndex}
                        className="text-xs bg-gray-100 text-gray-600 rounded-full px-2 py-1"
                      >
                        {feature}
                      </div>
                    ))}
                  </div>

                  {/* Arrow */}
                  <div className="flex justify-center">
                    <ArrowRightIcon className="h-4 w-4 text-gray-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all duration-200" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className={`bg-gray-50 px-6 py-3 border-t border-gray-100 transition-all duration-300 ${
          isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
        }`} style={{ transitionDelay: isOpen ? '300ms' : '0ms' }}>
          <Link
            to="/products"
            className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center justify-center transition-colors duration-200"
          >
            Ver todos los productos
            <ArrowRightIcon className="ml-2 h-3 w-3" />
          </Link>
        </div>
      </div>

      {/* Backdrop for mobile */}
      {isOpen && (
        <div className="fixed inset-0 z-10 md:hidden" onClick={() => setIsOpen(false)} />
      )}
    </div>
  );
};
